import React, { useState, useEffect, useCallback, useRef } from 'react';
import AiTag from '../components/AiTag';
import axios from 'axios';
import moment from 'moment';
import { Button, Input, Textarea, Select, Card, Pill, PageHeader } from '../lib/ui';
import { stagger } from '../lib/stagger';
import OutreachTab from '../components/OutreachTab';
import MarketsTab from '../components/MarketsTab';
import EmailActivityTab from '../components/EmailActivityTab';
import ReferralsTab from '../components/ReferralsTab';

const BASE = process.env.REACT_APP_BASE_URL || 'https://teardrophydro.co.uk/garageFarm';

const ipcRenderer = window.process?.type === 'renderer' ? window.require('electron').ipcRenderer : null;

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Inbox: axios instance (kept separate from the campaigns/outreach calls
// above, which use authHeaders() per-call instead of an instance) ───────────
const inboxClient = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`,
  headers: { 'Content-Type': 'application/json' },
});
inboxClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
inboxClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('accessToken');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);
const inboxPillStyle = { marginRight: 6, textTransform: 'uppercase' };

// ── Colours / tokens ──────────────────────────────────────────────────────────
const c = {
  bg:       'var(--bg)',
  card:     'var(--panel)',
  border:   'var(--border)',
  green:    'var(--brand-2)',
  greenDk:  'var(--brand-2-hover)',
  navy:     'var(--brand)',
  muted:    'var(--muted)',
  danger:   'var(--bad-fg)',
  pill:     'var(--panel-2)',
};

const label = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: c.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 6,
};

const inputStyle = { fontSize: 14, color: c.navy };

const SEGMENT_LABELS = {
  subscribers:    'All active subscribers',
  notify:         'Waitlist (all)',
  all:            'All (subscribers + waitlist)',
  buyers:         'Buyers of…',
  'notify-waiters': 'Waitlist for…',
};

// ── Banner ────────────────────────────────────────────────────────────────────
function Banner({ msg, onClose }) {
  if (!msg) return null;
  const isErr = msg.startsWith('Error') || msg.toLowerCase().includes('failed');
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 'var(--r-lg)', marginBottom: 16,
      background: isErr ? '#fef2f2' : '#f0fdf4',
      border: `1px solid ${isErr ? '#fca5a5' : '#86efac'}`,
      color: isErr ? '#991b1b' : '#166534',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      fontSize: 14,
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, lineHeight: 1 }}>×</button>
    </div>
  );
}

// ── Inbox: row renderers (Subscribers / Waitlist / Contact / Feedback) ────────

function NotifyRow({ r, i }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 200px',
        gap: 12,
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 14,
        alignItems: 'center',
        ...stagger(i),
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)', marginBottom: 2 }}>{r.email}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🌱</span>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{r.product}</span>
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <div style={{ marginBottom: 6 }}>
           <Pill bg="#fef3c7" fg="#92400e" style={inboxPillStyle}>Waitlist</Pill>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          {new Date(r.timestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ r, i }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 12,
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 14,
        ...stagger(i),
      }}
    >
      <div>
        <div style={{ fontWeight: 800 }}>{r.name}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>{r.email}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
          {new Date(r.submittedAt).toLocaleString()}
        </div>
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          {r.role && <Pill bg="#e0f2fe" style={inboxPillStyle}>Role: {r.role}</Pill>}
          {r.preferred && <Pill bg="#fef3c7" style={inboxPillStyle}>Pref: {r.preferred}</Pill>}
          {r.postcode && <Pill bg="#f1f5f9" style={inboxPillStyle}>{r.postcode}</Pill>}
        </div>
        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text)' }}>
          {r.message}
        </div>
      </div>
    </div>
  );
}

const ANSWER_STYLE = {
  'Great':            { bg: '#dcfce7', fg: '#166534' },
  'Good':             { bg: '#dbeafe', fg: '#1e40af' },
  'Could be better':  { bg: '#fef3c7', fg: '#92400e' },
  'Disappointed':     { bg: '#fee2e2', fg: '#b91c1c' },
};

const REORDER_STYLE = {
  yes:   { bg: '#dcfce7', fg: '#166534', label: 'Would reorder' },
  maybe: { bg: '#fef3c7', fg: '#92400e', label: 'Maybe reorder' },
  no:    { bg: '#fee2e2', fg: '#b91c1c', label: "Won't reorder" },
};

const FB_FIELDS = [
  { key: 'packaging', label: 'Packaging' },
  { key: 'produce',   label: 'Produce'   },
  { key: 'delivery',  label: 'Delivery'  },
  { key: 'value',     label: 'Value'     },
];

function FeedbackRow({ r, i }) {
  const reorder = r.wouldReorder ? REORDER_STYLE[r.wouldReorder] : null;
  return (
    <div
      style={{
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 14,
        display: 'grid',
        gap: 10,
        ...stagger(i),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {FB_FIELDS.map(f => {
            const answer = r[f.key];
            if (!answer) return null;
            const style = ANSWER_STYLE[answer] || { bg: '#f1f5f9', fg: '#475569' };
            return (
              <span key={f.key} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, background: style.bg, color: style.fg, fontWeight: 600 }}>
                {f.label}: {answer}
              </span>
            );
          })}
          {reorder && (
            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, background: reorder.bg, color: reorder.fg, fontWeight: 600 }}>
              {reorder.label}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>
          {new Date(r.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {r.comments && (
        <div style={{ fontSize: 13, color: 'var(--text)', borderTop: '1px solid var(--border)', paddingTop: 8, whiteSpace: 'pre-wrap' }}>
          {r.comments}
        </div>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  active:        { bg: '#dcfce7', fg: '#166534', label: 'Active' },
  unsubscribed:  { bg: '#f1f5f9', fg: '#475569', label: 'Unsubscribed' },
  hard_bounced:  { bg: '#fee2e2', fg: '#b91c1c', label: 'Hard Bounce' },
  soft_bounced:  { bg: '#fef3c7', fg: '#92400e', label: 'Soft Bounce' },
  complained:    { bg: '#fee2e2', fg: '#b91c1c', label: 'Complained' },
};

function SubscriberRow({ r, i, client, onArchive }) {
  const email = typeof r === 'string' ? r : r.email;
  const created = r.subscribedAt || r.timestamp || r.createdAt;
  const status = r.status || 'active';
  const statusStyle = STATUS_STYLE[status] || STATUS_STYLE.active;

  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const toggleOrders = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (orders !== null) return;
    setOrdersLoading(true);
    try {
      const res = await client.get(`/orders/by-customer?email=${encodeURIComponent(email)}`);
      setOrders(res.data || []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        overflow: 'hidden',
        ...stagger(i),
      }}
    >
      {/* Clickable header */}
      <div
        onClick={toggleOrders}
        style={{ padding: 14, cursor: 'pointer' }}
      >
        {/* Top row: email + status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)' }}>{email}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Pill bg={statusStyle.bg} fg={statusStyle.fg} style={inboxPillStyle}>{statusStyle.label}</Pill>
            {r.orderCount > 0 && <Pill bg="#ede9fe" fg="#5b21b6" style={inboxPillStyle}>🛒 {r.orderCount} order{r.orderCount !== 1 ? 's' : ''}</Pill>}
            {r.orderCount === 0 && <Pill bg="#f1f5f9" fg="#64748b" style={inboxPillStyle}>No orders</Pill>}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Archive ${email}? They'll stop being emailed but the record is kept, not deleted.`)) onArchive(r._id);
              }}
              style={{
                background: 'none', border: '1px solid #f0caca', color: '#b00020', borderRadius: 'var(--r-md)',
                fontSize: 11, padding: '3px 8px', cursor: 'pointer',
              }}
            >Archive</button>
            <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
          </div>
        </div>

        {/* Engagement stats */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--muted)' }}>
          <span>
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{r.totalOpens ?? 0}</span> opens
            {r.lastOpenedAt && <span style={{ marginLeft: 4 }}>· last {new Date(r.lastOpenedAt).toLocaleDateString()}</span>}
          </span>
          <span>
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{r.totalClicks ?? 0}</span> clicks
            {r.lastClickedAt && <span style={{ marginLeft: 4 }}>· last {new Date(r.lastClickedAt).toLocaleDateString()}</span>}
          </span>
          {r.softBounceCount > 0 && (
            <span style={{ color: '#f59e0b' }}>⚠ {r.softBounceCount} soft bounce{r.softBounceCount !== 1 ? 's' : ''}</span>
          )}
          {created && (
            <span style={{ marginLeft: 'auto' }}>Joined {new Date(created).toLocaleDateString()}</span>
          )}
        </div>
      </div>

      {/* Order history panel */}
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--panel-2)', padding: '12px 14px' }}>
          {ordersLoading && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading orders…</div>}
          {!ordersLoading && orders !== null && orders.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>No orders found for this email.</div>
          )}
          {!ordersLoading && orders && orders.length > 0 && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 2 }}>
                Order history - {orders.length} order{orders.length !== 1 ? 's' : ''}
              </div>
              {orders.map((order) => {
                const itemSummary = (order.items || [])
                  .filter(it => it.name !== 'Local delivery')
                  .map(it => it.quantity > 1 ? `${it.name} ×${it.quantity}` : it.name)
                  .join(', ');
                return (
                  <div key={order._id} style={{
                    background: 'var(--panel)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-lg)',
                    padding: '10px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{itemSummary || '-'}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{moment(order.timestamp).format('DD MMM YYYY, HH:mm')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>£{(order.total || 0).toFixed(2)}</span>
                      {order.fulfilled
                        ? <Pill bg="#dcfce7" fg="#166534" style={inboxPillStyle}>Fulfilled</Pill>
                        : <Pill bg="#f1f5f9" fg="#64748b" style={inboxPillStyle}>Pending</Pill>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function EmailStudio() {
  const [activeTab, setActiveTab] = useState('campaigns');

  // Template state
  const [templates, setTemplates]     = useState([]);
  const [selectedTpl, setSelectedTpl] = useState('');
  const [saveName, setSaveName]       = useState('');
  const [showSave, setShowSave]       = useState(false);

  // Editor state
  const [subject, setSubject]         = useState('');
  const [html, setHtml]               = useState('');

  // Segment state
  const [segment, setSegment]         = useState('subscribers');
  const [product, setProduct]         = useState('');
  const [products, setProducts]       = useState([]);
  const [notifyProducts, setNotifyProducts] = useState([]);
  const [count, setCount]             = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);

  // Send state
  const [testEmail, setTestEmail]     = useState('');
  const [sending, setSending]         = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [result, setResult]           = useState(null);

  // Feedback invite state
  const [fbDays, setFbDays]           = useState('30');
  const [recentCustomers, setRecentCustomers] = useState(null); // null = not loaded yet
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [sendingFb, setSendingFb]     = useState(false);
  const [fbResult, setFbResult]       = useState(null);
  const [fbTestEmail, setFbTestEmail] = useState('');
  const [sendingFbTest, setSendingFbTest] = useState(false);

  // Banner
  const [banner, setBanner]           = useState('');
  const previewRef = useRef(null);

  const flash = msg => { setBanner(msg); setTimeout(() => setBanner(''), 6000); };

  // Inbox tabs state (Subscribers / Waitlist / Contact / Feedback)
  const [inboxContact, setInboxContact]         = useState([]);
  const [inboxFeedback, setInboxFeedback]       = useState([]);
  const [inboxNotify, setInboxNotify]           = useState([]);
  const [inboxSubscribers, setInboxSubscribers] = useState([]);
  const [inboxLoading, setInboxLoading]         = useState(false);
  const [inboxError, setInboxError]             = useState('');
  const [subSort, setSubSort]                   = useState('orders_desc');

  const fetchInbox = useCallback(async () => {
    setInboxLoading(true);
    setInboxError('');
    try {
      const [c, f, n, s] = await Promise.all([
        inboxClient.get('/contact'),
        inboxClient.get('/feedback'),
        inboxClient.get('/notifications'),
        inboxClient.get('/subscribers'),
      ]);
      setInboxContact(c.data || []);
      setInboxFeedback(f.data || []);
      setInboxNotify(n.data || []);
      setInboxSubscribers(s.data || []);
    } catch {
      setInboxError('Failed to load messages.');
    } finally {
      setInboxLoading(false);
    }
  }, []);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  // Archives, not deletes - moved to a separate collection server-side (see
  // ArchivedSubscriber in routes/subscribers.js) so they stop being emailed
  // without the record being gone for good.
  const archiveSubscriber = async (id) => {
    try {
      await inboxClient.delete(`/subscribers/${id}`);
      setInboxSubscribers(rows => rows.filter(r => r._id !== id));
    } catch {
      flash('Failed to archive subscriber');
    }
  };

  // Never opened AND never clicked AND never ordered - the safe bulk-archive
  // set. Anyone with an order stays, even if their email tracking shows
  // nothing (some clients block tracking pixels outright), since archiving a
  // real customer's record is a much costlier mistake than keeping a dead one.
  const neverEngagedSubscribers = inboxSubscribers.filter(
    r => !r.lastOpenedAt && !r.lastClickedAt && (r.orderCount || 0) === 0
  );

  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [showNeverEngagedPreview, setShowNeverEngagedPreview] = useState(false);
  const bulkArchiveNeverEngaged = async () => {
    if (neverEngagedSubscribers.length === 0) return;
    if (!window.confirm(
      `Archive ${neverEngagedSubscribers.length} subscriber(s) who've never opened or clicked an email and have no orders? They'll stop being emailed but the records are kept, not deleted.`
    )) return;
    setBulkArchiving(true);
    try {
      const ids = neverEngagedSubscribers.map(r => r._id);
      const res = await inboxClient.post('/subscribers/bulk-delete', { ids });
      const archivedIds = new Set(ids);
      setInboxSubscribers(rows => rows.filter(r => !archivedIds.has(r._id)));
      setShowNeverEngagedPreview(false);
      flash(`Archived ${res.data.deletedCount} subscriber(s)`);
    } catch {
      flash('Bulk archive failed');
    } finally {
      setBulkArchiving(false);
    }
  };

  const getInboxRows = () => {
    if (activeTab === 'contact') return inboxContact;
    if (activeTab === 'feedback') return inboxFeedback;
    if (activeTab === 'notify') return inboxNotify;
    return inboxSubscribers;
  };

  // ── Disk sync (lets Claude read/redo the draft on this machine live) ──────
  const draftMtimeRef = useRef(0);
  const [draftLoaded, setDraftLoaded] = useState(!ipcRenderer);

  // Load whatever draft is on disk when Email Studio opens
  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.invoke('mailer-draft-get').then(draft => {
      if (draft) {
        setSubject(draft.subject || '');
        setHtml(draft.html || '');
        draftMtimeRef.current = draft.mtimeMs;
      }
      setDraftLoaded(true);
    });
  }, []);

  // Mirror the editor to disk (debounced) so it can be read/edited outside the app
  useEffect(() => {
    if (!ipcRenderer || !draftLoaded) return;
    const t = setTimeout(() => {
      ipcRenderer.invoke('mailer-draft-set', { subject, html }).then(res => {
        if (res?.mtimeMs) draftMtimeRef.current = res.mtimeMs;
      });
    }, 600);
    return () => clearTimeout(t);
  }, [subject, html, draftLoaded]);

  // Pick up edits made to the draft file from outside the app (e.g. by Claude)
  useEffect(() => {
    if (!ipcRenderer) return;
    const iv = setInterval(() => {
      ipcRenderer.invoke('mailer-draft-get').then(draft => {
        if (draft && draft.mtimeMs > draftMtimeRef.current) {
          draftMtimeRef.current = draft.mtimeMs;
          setSubject(draft.subject || '');
          setHtml(draft.html || '');
          flash('Editor updated from disk');
        }
      });
    }, 1500);
    return () => clearInterval(iv);
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BASE}/email/templates`, { headers: authHeaders() });
      setTemplates(data);
    } catch { /* silently ignore */ }
  }, []);

  useEffect(() => {
    loadTemplates();
    axios.get(`${BASE}/email/products`,        { headers: authHeaders() }).then(r => setProducts(r.data)).catch(() => {});
    axios.get(`${BASE}/email/notify-products`, { headers: authHeaders() }).then(r => setNotifyProducts(r.data)).catch(() => {});
  }, [loadTemplates]);

  // Refresh count whenever segment/product changes
  useEffect(() => {
    if (!segment) return;
    const needsProduct = segment === 'buyers' || segment === 'notify-waiters';
    if (needsProduct && !product) { setCount(null); return; }
    setLoadingCount(true);
    axios.get(`${BASE}/email/segment-count`, { headers: authHeaders(), params: { segment, product: needsProduct ? product : undefined } })
      .then(r => setCount(r.data.count))
      .catch(() => setCount(null))
      .finally(() => setLoadingCount(false));
  }, [segment, product]);

  // ── Template actions ──────────────────────────────────────────────────────

  const loadTemplate = () => {
    const t = templates.find(t => t._id === selectedTpl);
    if (!t) return;
    setSubject(t.subject || '');
    setHtml(t.html || '');
    flash(`Loaded "${t.name}"`);
  };

  const saveTemplate = async () => {
    if (!saveName.trim()) return flash('Error: Please enter a template name');
    if (!html)           return flash('Error: Template has no HTML content');
    try {
      await axios.post(`${BASE}/email/templates`, { name: saveName.trim(), subject, html }, { headers: authHeaders() });
      await loadTemplates();
      setShowSave(false);
      setSaveName('');
      flash(`Saved "${saveName.trim()}"`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await axios.delete(`${BASE}/email/templates/${id}`, { headers: authHeaders() });
      await loadTemplates();
      if (selectedTpl === id) setSelectedTpl('');
      flash(`Deleted "${name}"`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Sending ───────────────────────────────────────────────────────────────

  const sendTest = async () => {
    if (!html || !subject) return flash('Error: Add a subject and HTML before sending a test');
    if (!testEmail)        return flash('Error: Enter a test email address');
    setSendingTest(true);
    try {
      await axios.post(`${BASE}/email/send-test`, { html, subject, testEmail }, { headers: authHeaders() });
      flash(`Test sent to ${testEmail}`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSendingTest(false);
    }
  };

  const sendBulk = async () => {
    if (!html || !subject) return flash('Error: Add a subject and HTML before sending');
    if (!count)            return flash('Error: No recipients in this segment');
    const needsProduct = segment === 'buyers' || segment === 'notify-waiters';
    if (needsProduct && !product) return flash('Error: Select a product for this segment');
    if (!window.confirm(`Send to ${count} recipients? This cannot be undone.`)) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${BASE}/email/send`, {
        html, subject, segment,
        product: needsProduct ? product : undefined,
      }, { headers: authHeaders() });
      setResult(data);
      flash(`Done - ${data.sent} sent, ${data.failed} failed`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const loadRecentCustomers = async () => {
    setLoadingCustomers(true);
    setRecentCustomers(null);
    setSelectedEmails(new Set());
    setFbResult(null);
    try {
      const { data } = await axios.get(`${BASE}/email/recent-orders`, {
        headers: authHeaders(),
        params:  { days: fbDays },
      });
      setRecentCustomers(data);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const toggleCustomer = email => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const selectAll   = () => setSelectedEmails(new Set((recentCustomers || []).map(c => c.email)));
  const deselectAll = () => setSelectedEmails(new Set());

  const sendFbTest = async () => {
    if (!fbTestEmail) return flash('Error: Enter a test email address');
    setSendingFbTest(true);
    try {
      await axios.post(`${BASE}/email/send-feedback`, { email: fbTestEmail, name: 'Test' }, { headers: authHeaders() });
      flash(`Test feedback email sent to ${fbTestEmail}`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSendingFbTest(false);
    }
  };

  const sendFeedback = async () => {
    if (!selectedEmails.size) return flash('Error: Select at least one customer');
    setSendingFb(true);
    setFbResult(null);
    let sent = 0, failed = 0, errors = [];
    for (const email of selectedEmails) {
      const customer = recentCustomers.find(c => c.email === email);
      try {
        await axios.post(`${BASE}/email/send-feedback`, {
          email,
          name: customer?.name || undefined,
        }, { headers: authHeaders() });
        sent++;
      } catch (e) {
        failed++;
        errors.push({ email, error: e.response?.data?.error || e.message });
      }
    }
    setFbResult({ sent, failed, errors });
    setSelectedEmails(new Set());
    flash(`Done - ${sent} sent${failed ? `, ${failed} failed` : ''}`);
    setSendingFb(false);
  };

  // ── Preview HTML ──────────────────────────────────────────────────────────
  const previewHtml = html
    .replace(/\{\{name\}\}/g,        'Alex')
    .replace(/\{\{email\}\}/g,       'alex@example.com')
    .replace(/\{\{unsubscribe\}\}/g, '#');

  const needsProduct = segment === 'buyers' || segment === 'notify-waiters';
  const productList  = segment === 'buyers' ? products : notifyProducts;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Marketing"
        subtitle="Campaigns, outreach and the inbox"
        tabs={[
          { key: 'campaigns', label: 'Email campaigns' },
          { key: 'outreach',  label: 'Outreach' },
          { key: 'markets',   label: 'Markets' },
          { key: 'referrals', label: 'Referrals' },
          { key: 'activity',  label: 'Activity' },
          { divider: true },
          { key: 'subscribers', label: 'Subscribers', count: inboxSubscribers.length },
          { key: 'notify',      label: 'Waitlist',    count: inboxNotify.length },
          { key: 'contact',     label: 'Contact',     count: inboxContact.length },
          { key: 'feedback',    label: 'Feedback',    count: inboxFeedback.length },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'outreach' ? (
        <OutreachTab />
      ) : activeTab === 'markets' ? (
        <MarketsTab />
      ) : activeTab === 'referrals' ? (
        <ReferralsTab />
      ) : activeTab === 'activity' ? (
        <EmailActivityTab />
      ) : (['subscribers', 'notify', 'contact', 'feedback'].includes(activeTab)) ? (
        <>
          {activeTab === 'subscribers' && (
            <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <label style={{ ...label, display: 'inline-block', marginBottom: 0, marginRight: 8 }}>Sort</label>
                <select
                  value={subSort}
                  onChange={e => setSubSort(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    border: `1px solid ${c.border}`,
                    borderRadius: 'var(--r-md)',
                    background: '#fff',
                    color: c.navy,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <option value="orders_desc">Most orders first</option>
                  <option value="orders_asc">Fewest orders first</option>
                  <option value="joined_desc">Newest first</option>
                  <option value="joined_asc">Oldest first</option>
                  <option value="alpha">Alphabetical</option>
                </select>
              </div>
              {neverEngagedSubscribers.length > 0 && (
                <Button
                  onClick={() => setShowNeverEngagedPreview(v => !v)}
                  style={{ fontSize: 12, marginLeft: 'auto' }}
                >
                  {showNeverEngagedPreview ? 'Hide' : 'Review'} {neverEngagedSubscribers.length} never-opened, no-order subscriber(s)
                </Button>
              )}
            </div>
          )}

          {activeTab === 'subscribers' && showNeverEngagedPreview && (
            <div style={{
              background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
              padding: '14px 16px', marginBottom: 14,
            }}>
              <div style={{ fontSize: 12, color: c.muted, marginBottom: 10 }}>
                These {neverEngagedSubscribers.length} subscriber(s) have never opened or clicked an email and have no orders on file.
                Archiving moves them out of the mailing list - the record is kept, not deleted.
              </div>
              <div style={{ display: 'grid', gap: 6, maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
                {neverEngagedSubscribers.map(r => (
                  <div key={r._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, padding: '4px 0', borderBottom: `1px solid ${c.border}` }}>
                    <span style={{ color: c.navy }}>{r.email}</span>
                    <span style={{ color: c.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                      Joined {(r.subscribedAt || r.timestamp || r.createdAt) ? new Date(r.subscribedAt || r.timestamp || r.createdAt).toLocaleDateString() : 'unknown'}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="danger"
                  onClick={bulkArchiveNeverEngaged}
                  disabled={bulkArchiving}
                  style={{ fontSize: 12 }}
                >
                  {bulkArchiving ? 'Archiving…' : `Archive all ${neverEngagedSubscribers.length}`}
                </Button>
                <Button onClick={() => setShowNeverEngagedPreview(false)} style={{ fontSize: 12 }}>Cancel</Button>
              </div>
            </div>
          )}

          {inboxLoading && <div style={{ marginBottom: 12, color: c.muted, fontSize: 13 }}>Loading…</div>}
          {inboxError && <div style={{ marginBottom: 12, color: '#b91c1c', fontSize: 13 }}>{inboxError}</div>}

          <div style={{ display: 'grid', gap: 10 }}>
            {(() => {
              const rows = getInboxRows();
              if (rows.length === 0 && !inboxLoading) {
                return (
                  <div style={{ background: '#fff', border: `1px solid ${c.border}`, borderRadius: 'var(--r-lg)', padding: 16, color: c.muted, fontSize: 13 }}>
                    No entries yet.
                  </div>
                );
              }
              const sorted = activeTab === 'subscribers'
                ? rows.slice().sort((a, b) => {
                    const dateA = new Date(a.subscribedAt || a.timestamp || a.createdAt || 0);
                    const dateB = new Date(b.subscribedAt || b.timestamp || b.createdAt || 0);
                    if (subSort === 'orders_desc') return (b.orderCount ?? 0) - (a.orderCount ?? 0);
                    if (subSort === 'orders_asc')  return (a.orderCount ?? 0) - (b.orderCount ?? 0);
                    if (subSort === 'joined_desc') return dateB - dateA;
                    if (subSort === 'joined_asc')  return dateA - dateB;
                    if (subSort === 'alpha')        return (a.email || '').localeCompare(b.email || '');
                    return 0;
                  })
                : rows.slice().reverse();
              return sorted.map((r, i) => {
                if (activeTab === 'contact') return <ContactRow key={i} r={r} i={i} />;
                if (activeTab === 'feedback') return <FeedbackRow key={i} r={r} i={i} />;
                if (activeTab === 'notify') return <NotifyRow key={i} r={r} i={i} />;
                return <SubscriberRow key={i} r={r} i={i} client={inboxClient} onArchive={archiveSubscriber} />;
              });
            })()}
          </div>
        </>
      ) : (
      <>
      <Banner msg={banner} onClose={() => setBanner('')} />

      {/* ── Template library ── */}
      <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 14px', color: c.navy, fontSize: 16 }}>Templates</h3>
        {templates.length === 0 ? (
          <p style={{ color: c.muted, fontSize: 14, margin: 0 }}>No saved templates yet. Build something and save it below.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {templates.map(t => (
              <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: c.pill, border: `1px solid ${c.border}`, borderRadius: 'var(--r-md)', padding: '8px 12px' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: c.navy, fontSize: 14 }}>{t.name}</span><AiTag record={t} />
                  {t.subject && <span style={{ color: c.muted, fontSize: 13, marginLeft: 10 }}>{t.subject}</span>}
                  <span style={{ color: c.muted, fontSize: 12, marginLeft: 10 }}>{new Date(t.updatedAt).toLocaleDateString('en-GB')}</span>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => { setSubject(t.subject || ''); setHtml(t.html || ''); flash(`Loaded "${t.name}"`); }}
                >
                  Load
                </Button>
                <Button variant="danger" onClick={() => deleteTemplate(t._id, t.name)}>Delete</Button>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {showSave ? (
            <>
              <Input
                style={{ ...inputStyle, width: 260 }}
                placeholder="Template name…"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveTemplate()}
                autoFocus
              />
              <Button variant="primary" onClick={saveTemplate}>Save</Button>
              <Button variant="muted" onClick={() => { setShowSave(false); setSaveName(''); }}>Cancel</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setShowSave(true)} disabled={!html}>Save current as…</Button>
              <Button variant="muted" onClick={() => { setSubject(''); setHtml(''); flash('Cleared'); }}>New / Clear</Button>
            </>
          )}
        </div>
      </Card>

      {/* ── Editor + Preview ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Editor */}
        <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 14px', color: c.navy, fontSize: 16 }}>
            Editor
            <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 10, color: ipcRenderer ? c.green : c.muted }}>
              {ipcRenderer ? '● synced to disk (.mailer-draft.json)' : 'not synced — run in the desktop app to sync'}
            </span>
          </h3>
          <div style={{ marginBottom: 12 }}>
            <label style={label}>Subject line</label>
            <Input
              style={inputStyle}
              placeholder="e.g. New harvest this week - grab yours"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </div>
          <div>
            <label style={label}>
              HTML body
              <span style={{ color: c.muted, fontWeight: 400, marginLeft: 8 }}>
                use {'{{name}}'}, {'{{email}}'}, {'{{unsubscribe}}'}
              </span>
            </label>
            <Textarea
              style={{
                ...inputStyle,
                height: 480,
                fontFamily: 'ui-monospace, monospace',
                fontSize: 13,
                resize: 'vertical',
                lineHeight: 1.5,
              }}
              placeholder="Paste or write your email HTML here…"
              value={html}
              onChange={e => setHtml(e.target.value)}
              spellCheck={false}
            />
          </div>
        </Card>

        {/* Preview */}
        <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 14px', color: c.navy, fontSize: 16 }}>
            Preview
            <span style={{ color: c.muted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              (name → Alex, email → alex@example.com)
            </span>
          </h3>
          {html ? (
            <iframe
              ref={previewRef}
              title="Email preview"
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              style={{
                width: '100%',
                height: 530,
                border: `1px solid ${c.border}`,
                borderRadius: 'var(--r-md)',
                background: '#fff',
              }}
            />
          ) : (
            <div style={{
              height: 530,
              border: `1px dashed ${c.border}`,
              borderRadius: 'var(--r-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: c.muted,
              fontSize: 14,
            }}>
              Preview appears here when you add HTML
            </div>
          )}
        </Card>
      </div>

      {/* ── Segment + Send ── */}
      <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px', color: c.navy, fontSize: 16 }}>Send Campaign</h3>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div style={{ minWidth: 240 }}>
            <label style={label}>Audience</label>
            <Select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={segment}
              onChange={e => { setSegment(e.target.value); setProduct(''); }}
            >
              {Object.entries(SEGMENT_LABELS).map(([val, lbl]) => (
                <option key={val} value={val}>{lbl}</option>
              ))}
            </Select>
          </div>

          {needsProduct && (
            <div style={{ minWidth: 220 }}>
              <label style={label}>Product</label>
              <Select
                style={{ ...inputStyle, cursor: 'pointer' }}
                value={product}
                onChange={e => setProduct(e.target.value)}
              >
                <option value="">- choose product -</option>
                {productList.map(p => <option key={p} value={p}>{p}</option>)}
              </Select>
            </div>
          )}

          <div style={{ alignSelf: 'flex-end' }}>
            <span style={{
              display: 'inline-block',
              background: c.pill,
              border: `1px solid ${c.border}`,
              borderRadius: 999,
              padding: '7px 14px',
              fontSize: 14,
              color: c.navy,
              fontWeight: 600,
            }}>
              {loadingCount ? '…' : count === null ? '-' : `${count} recipients`}
            </span>
          </div>
        </div>

        {/* Step 1 - Test */}
        <div style={{ paddingTop: 14, marginTop: 4, borderTop: `1px solid ${c.border}` }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Step 1 - Send a test to yourself first
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              style={{ ...inputStyle, maxWidth: 300 }}
              placeholder="your@email.com"
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendTest()}
            />
            <Button variant="ghost" onClick={sendTest} disabled={sendingTest}>
              {sendingTest ? 'Sending…' : 'Send test email'}
            </Button>
          </div>
        </div>

        {/* Step 2 - Bulk send */}
        <div style={{ paddingTop: 14, marginTop: 14, borderTop: `1px solid ${c.border}` }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Step 2 - Send to everyone
          </p>
          <Button
            variant="primary"
            style={{ minWidth: 220 }}
            onClick={sendBulk}
            disabled={sending || !count || !html || !subject}
          >
            {sending ? 'Sending…' : `Send to ${count ?? '?'} recipients`}
          </Button>
        </div>

        {result && (
          <div style={{ marginTop: 14, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 'var(--r-lg)', fontSize: 14, color: '#166534' }}>
            Last send: {result.sent} sent · {result.failed} failed · {result.total} total
            {result.errors?.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer' }}>{result.errors.length} failures</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {result.errors.map((e, i) => <li key={i}>{e.email}: {e.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>

      {/* ── Feedback Requests ── */}
      <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px', color: c.navy, fontSize: 16 }}>Feedback Requests</h3>
        <p style={{ color: c.muted, fontSize: 14, margin: '0 0 16px' }}>
          Ask customers who've ordered recently for a <strong>Google review</strong>.
        </p>

        {/* Test send */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 14, marginBottom: 14, borderBottom: `1px solid ${c.border}` }}>
          <Input
            style={{ ...inputStyle, maxWidth: 280 }}
            type="email"
            placeholder="Send a test to yourself…"
            value={fbTestEmail}
            onChange={e => setFbTestEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendFbTest()}
          />
          <Button variant="ghost" onClick={sendFbTest} disabled={sendingFbTest}>
            {sendingFbTest ? 'Sending…' : 'Send test'}
          </Button>
        </div>

        {/* Controls row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <label style={{ ...label, display: 'inline-block', marginBottom: 0, marginRight: 8 }}>Orders in last</label>
            <Select
              style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}
              value={fbDays}
              onChange={e => { setFbDays(e.target.value); setRecentCustomers(null); setSelectedEmails(new Set()); }}
            >
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
            </Select>
          </div>
          <Button variant="ghost" onClick={loadRecentCustomers} disabled={loadingCustomers}>
            {loadingCustomers ? 'Loading…' : 'Load customers'}
          </Button>
        </div>

        {/* Customer list */}
        {recentCustomers !== null && (
          recentCustomers.length === 0 ? (
            <p style={{ color: c.muted, fontSize: 14 }}>No orders found in this period.</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: c.muted }}>
                  {recentCustomers.length} customer{recentCustomers.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                  {selectedEmails.size} selected
                </span>
                <Button variant="muted" onClick={selectedEmails.size === recentCustomers.length ? deselectAll : selectAll}>
                  {selectedEmails.size === recentCustomers.length ? 'Deselect all' : 'Select all'}
                </Button>
              </div>

              <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: 'var(--r-lg)', marginBottom: 14 }}>
                {recentCustomers.map(customer => (
                  <label
                    key={customer.email}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px',
                      borderBottom: `1px solid ${c.border}`,
                      cursor: 'pointer',
                      background: selectedEmails.has(customer.email) ? `color-mix(in srgb, ${c.green} 8%, white)` : 'white',
                      transition: 'background .1s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmails.has(customer.email)}
                      onChange={() => toggleCustomer(customer.email)}
                      style={{ width: 16, height: 16, accentColor: c.green, flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: c.navy }}>
                        {customer.name || <span style={{ color: c.muted, fontWeight: 400 }}>No name</span>}
                      </div>
                      <div style={{ fontSize: 12, color: c.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer.email}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: c.muted, flexShrink: 0 }}>
                      {new Date(customer.lastOrderAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                  </label>
                ))}
              </div>

              <Button
                variant="primary"
                onClick={sendFeedback}
                disabled={sendingFb || !selectedEmails.size}
              >
                {sendingFb ? 'Sending…' : `Send to ${selectedEmails.size} customer${selectedEmails.size !== 1 ? 's' : ''}`}
              </Button>

              {fbResult && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 'var(--r-lg)', fontSize: 13, color: '#166534' }}>
                  {fbResult.sent} sent · {fbResult.failed} failed
                  {fbResult.errors?.length > 0 && (
                    <details style={{ marginTop: 4 }}>
                      <summary style={{ cursor: 'pointer' }}>See failures</summary>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                        {fbResult.errors.map((e, i) => <li key={i}>{e.email}: {e.error}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </>
          )
        )}
      </Card>
      </>
      )}
    </div>
  );
}
